/**
 * Dioptric powers, written the way an optician writes them.
 *
 * Two decimal places and the sign in front — -2.00, -2.50, +1.25 — because a dropped zero or a
 * missing plus is exactly how a lab misreads a job. The same discipline as PD and money: a decimal
 * string on the wire, integer hundredths internally, and never a binary float in between.
 */

/** Hundredths of a dioptre. -250 is -2.50 D. */
export type DioptreHundredths = number;

/** Sphere and cylinder: an optional sign, then up to two decimals. The explicit plus is how a positive power is written. */
export const SIGNED_POWER_PATTERN = /^[+-]?\d{1,2}(\.\d{1,2})?$/;
/** A reading addition is always positive, so it may carry a plus but never a minus. */
export const ADDITION_PATTERN = /^\+?\d{1,2}(\.\d{1,2})?$/;
/** Prism is a magnitude; its base carries the direction, so it takes no sign at all. */
export const PRISM_PATTERN = /^\d{1,2}(\.\d{1,2})?$/;

/** Reads a decimal string as hundredths, or null when it is not a power. Digits are read as integers, never as a fraction. */
export function parseDioptreToHundredths(value: string): DioptreHundredths | null {
  const trimmed = value.trim();
  if (!SIGNED_POWER_PATTERN.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = trimmed.replace(/^[+-]/, '').split('.');
  const hundredths = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));

  // -0.00 is plano, and plano has no sign.
  return negative && hundredths !== 0 ? -hundredths : hundredths;
}

/** Quarter-dioptre steps, decided on integer hundredths: -2.25 is -225, a multiple of 25. No binary fraction is ever involved. */
export function isQuarterDioptre(hundredths: DioptreHundredths): boolean {
  return Number.isInteger(hundredths) && hundredths % 25 === 0;
}

/** Hundredths as the plain decimal string a database column takes: -2.25, 0.00, 1.50. Integer arithmetic only. */
export function hundredthsToDecimalString(hundredths: DioptreHundredths): string {
  const sign = hundredths < 0 ? '-' : '';
  return `${sign}${magnitude(hundredths)}`;
}

/** Writes hundredths as a signed power: two decimals, plus or minus in front, and plano as 0.00. */
export function formatSignedHundredths(hundredths: DioptreHundredths): string {
  const sign = hundredths > 0 ? '+' : hundredths < 0 ? '-' : '';
  return `${sign}${magnitude(hundredths)}`;
}

/**
 * A sphere, cylinder or addition in optical notation.
 *
 * Takes whatever the database or the wire holds — Prisma's `Decimal` drops trailing zeros, so a
 * stored -2.50 arrives as "-2.5" — and gives back the notation a lab reads. A value that somehow is
 * not a power is returned as it came rather than dropped: a clinical figure must never vanish from
 * the screen because it failed to format.
 */
export function formatDioptre(value: { toString(): string } | null | undefined): string | null {
  if (value === null || value === undefined) return null;

  const raw = value.toString();
  const hundredths = parseDioptreToHundredths(raw);
  return hundredths === null ? raw : formatSignedHundredths(hundredths);
}

/** A prism in dioptres: two decimals and no sign, because the base already says which way it points. */
export function formatPrismDioptre(
  value: { toString(): string } | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;

  const raw = value.toString();
  const hundredths = PRISM_PATTERN.test(raw.trim()) ? parseDioptreToHundredths(raw) : null;
  return hundredths === null ? raw : magnitude(hundredths);
}

function magnitude(hundredths: DioptreHundredths): string {
  const absolute = Math.abs(hundredths);
  return `${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}
